import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  RefreshCw,
  FileText,
  MessageCircle,
  RotateCcw,
  Loader2,
  Search,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/auth';
import { Sidebar } from '@/components/common/Sidebar';
import { ThemeToggle } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { listDocuments, type DocumentDTO } from '@/services/documents';
import { isMemedPrescriptionRenewable } from '@/lib/prescriptionRenewal';
import { isDocumentIssuedByDoctor } from '@/lib/documentOwnership';
import {
  getPrescriptionValidity,
  isPrescriptionDocumentType,
  urgencyBadgeClass,
  urgencyCardClass,
  type PrescriptionValidityUrgency,
} from '@/lib/prescriptionValidity';

type ValidityFilter = 'all' | 'expiring' | 'expired' | 'ok';

type PrescriptionRow = {
  doc: DocumentDTO;
  patientName: string;
  patientId: string | null;
  issuedAt: Date;
  validity: ReturnType<typeof getPrescriptionValidity>;
  canRenew: boolean;
};

function patientNameFromDoc(doc: DocumentDTO): string {
  return (
    doc.appointment?.patient?.name ||
    doc.appointment?.patient?.user?.name ||
    'Paciente'
  );
}

function patientIdFromDoc(doc: DocumentDTO): string | null {
  return (
    doc.appointment?.patientId ||
    doc.appointment?.patient?.id ||
    doc.appointment?.patient?.user?.id ||
    null
  );
}

export default function FilaRenovacoesPage() {
  const navigate = useNavigate();
  const { user, isLoading, logout } = useAuth();
  const [documents, setDocuments] = useState<DocumentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<ValidityFilter>('all');
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    } else if (!isLoading && user && user.role !== UserRole.DOCTOR) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  const loadPrescriptions = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await listDocuments();
      if (res.success && Array.isArray(res.data)) {
        setDocuments(
          res.data.filter(
            (d) =>
              isPrescriptionDocumentType(d.documentType) &&
              isDocumentIssuedByDoctor(d, user.id),
          ),
        );
      } else {
        setDocuments([]);
        toast.error(res.error || 'Erro ao carregar receitas');
      }
    } catch {
      setDocuments([]);
      toast.error('Erro ao carregar histórico de receitas');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.role === UserRole.DOCTOR) {
      void loadPrescriptions();
    }
  }, [user, loadPrescriptions]);

  const rows: PrescriptionRow[] = useMemo(() => {
    return documents
      .map((doc) => {
        const validity = getPrescriptionValidity(doc.expiresAt);
        return {
          doc,
          patientName: patientNameFromDoc(doc),
          patientId: patientIdFromDoc(doc),
          issuedAt: new Date(doc.createdAt),
          validity,
          canRenew: isMemedPrescriptionRenewable(doc),
        };
      })
      .sort((a, b) => {
        const aDays = a.validity.daysRemaining;
        const bDays = b.validity.daysRemaining;
        if (aDays === null && bDays === null) {
          return b.issuedAt.getTime() - a.issuedAt.getTime();
        }
        if (aDays === null) return 1;
        if (bDays === null) return -1;
        return aDays - bDays;
      });
  }, [documents]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return rows.filter((row) => {
      if (term && !row.patientName.toLowerCase().includes(term)) return false;
      if (filter === 'expiring') {
        return (
          row.validity.urgency === 'warning' || row.validity.urgency === 'critical'
        );
      }
      if (filter === 'expired') return row.validity.urgency === 'expired';
      if (filter === 'ok') return row.validity.urgency === 'ok';
      return true;
    });
  }, [rows, searchTerm, filter]);

  const counts = useMemo(() => {
    const base = { all: rows.length, expiring: 0, expired: 0, ok: 0 };
    for (const row of rows) {
      if (row.validity.urgency === 'expired') base.expired += 1;
      else if (
        row.validity.urgency === 'warning' ||
        row.validity.urgency === 'critical'
      ) {
        base.expiring += 1;
      } else if (row.validity.urgency === 'ok') base.ok += 1;
    }
    return base;
  }, [rows]);

  const openChat = async (row: PrescriptionRow, renew: boolean) => {
    if (!row.doc.appointmentId) {
      toast.error('Consulta vinculada não encontrada.');
      return;
    }
    setBusyId(row.doc.id);
    try {
      const params = new URLSearchParams({ tab: 'documents' });
      if (renew) params.set('renew', row.doc.id);
      navigate(`/chat/${row.doc.appointmentId}?${params.toString()}`, {
        state: { from: '/fila-renovacoes' },
      });
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading || !user) return null;

  const filterButtons: Array<{ id: ValidityFilter; label: string; count: number }> = [
    { id: 'all', label: 'Todas', count: counts.all },
    { id: 'expiring', label: 'A vencer', count: counts.expiring },
    { id: 'expired', label: 'Vencidas', count: counts.expired },
    { id: 'ok', label: 'Com validade', count: counts.ok },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <Sidebar
        userRole={user.role}
        userName={user.name}
        onLogout={async () => {
          await logout();
          navigate('/login');
        }}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-6 md:px-8 py-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Receitas e renovações
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Acompanhe validade, dias restantes e renove ou fale com o paciente.
            </p>
          </div>
          <ThemeToggle />
        </header>

        <main className="flex-1 p-6 md:p-8 space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {filterButtons.map((btn) => (
                <Button
                  key={btn.id}
                  size="sm"
                  variant={filter === btn.id ? 'default' : 'outline'}
                  onClick={() => setFilter(btn.id)}
                >
                  {btn.label}
                  <span className="ml-2 text-xs opacity-80">{btn.count}</span>
                </Button>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar paciente..."
                  className="pl-10"
                />
              </div>
              <Button onClick={() => void loadPrescriptions()} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
              </Button>
            </div>
          </div>

          {counts.expiring > 0 && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <p>
                {counts.expiring === 1
                  ? 'Há 1 receita próxima do vencimento.'
                  : `Há ${counts.expiring} receitas próximas do vencimento.`}{' '}
                Renove via Memed ou fale com o paciente.
              </p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Carregando receitas...
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="text-center py-16 bg-white dark:bg-neutral-900 rounded-lg border border-dashed">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {rows.length === 0
                  ? 'Nenhuma receita emitida ainda.'
                  : 'Nenhuma receita neste filtro.'}
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredRows.map((row) => {
                const urgency = row.validity.urgency as PrescriptionValidityUrgency;
                const issuedLabel = format(row.issuedAt, "dd/MM/yyyy 'às' HH:mm", {
                  locale: ptBR,
                });
                const expiresLabel = row.validity.expiresAt
                  ? format(row.validity.expiresAt, 'dd/MM/yyyy', { locale: ptBR })
                  : null;
                const isBusy = busyId === row.doc.id;

                return (
                  <div
                    key={row.doc.id}
                    className={`rounded-lg border p-4 md:p-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between ${urgencyCardClass(urgency)}`}
                  >
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="font-semibold text-lg text-gray-900 dark:text-white truncate">
                          {row.patientName}
                        </h2>
                        <span
                          className={`px-2 py-0.5 text-xs font-semibold rounded ${urgencyBadgeClass(urgency)}`}
                        >
                          {row.validity.label}
                        </span>
                        {row.doc.provider === 'MEMED' && (
                          <span className="px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            Memed
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 flex flex-col sm:flex-row sm:flex-wrap gap-x-4 gap-y-1">
                        <span>Emitida em {issuedLabel}</span>
                        {expiresLabel && <span>Válida até {expiresLabel}</span>}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isBusy}
                        onClick={() => void openChat(row, false)}
                      >
                        {isBusy ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <MessageCircle className="w-4 h-4 mr-2" />
                        )}
                        Falar
                      </Button>
                      <Button
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-700"
                        disabled={isBusy || !row.canRenew}
                        title={
                          row.canRenew
                            ? 'Abrir renovação no Memed'
                            : 'Renovação Memed disponível apenas para receitas digitais elegíveis'
                        }
                        onClick={() => void openChat(row, true)}
                      >
                        {isBusy ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RotateCcw className="w-4 h-4 mr-2" />
                        )}
                        Renovar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
